"use client"


import { Button } from "../components/ui/button"
import { Copy, Eye, X } from "lucide-react";
import { useState } from "react";
import useSWRMutation from "swr/mutation";
import { useToast } from "../hooks/use-toast";
import Switch from "./Switch";
import { useNavigate } from "react-router-dom";
import { useCurrency } from "../hooks/useCurrency";
import { message, Popover, Radio } from "antd";
import { handleMutation, handleUpdates } from "../util/axiosConfig";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import dayjs from "dayjs";

export function OwnerDetailsCard({ selectedUser }:{ selectedUser: FleetOwner }) {
  const [switchValue, setSwitchValue] = useState<boolean>(selectedUser.approved === 'APPROVED');
  const [payoutFrequency, setPayoutFrequency] = useState<FleetOwner['payoutFrequency']>(selectedUser.payoutFrequency || 'weekly');
  const navigate = useNavigate()
  const { formatLargeNumber } = useCurrency();

  const { trigger: toggleStatus, isMutating: updatingStatus } = useSWRMutation(`newAdmin/users/fleetowner/${selectedUser?.id}/toggle-status`, handleUpdates);

  const { trigger: updatePayoutFrequency, isMutating: updatingFrequency } = useSWRMutation(`newAdmin/fleet-owner/payout-frequency/${selectedUser?.id}`, handleMutation);

  const toggleSwitch =  async () => {
    if (!selectedUser) return;
    try {
      const response = await toggleStatus({
        status: switchValue ? "inactive" : "active",
      });
      message.success("User Status updated");
    } catch (error) {
      console.error("Error updating status:", error);
    }
  }

  const updateFrequencyValue = async (value: FleetOwner['payoutFrequency']) => {
    if (!selectedUser) return;
    try {
      const response = await updatePayoutFrequency({
        payoutFrequency: value,
      });
      message.success("Payout Frequency updated");
    } catch (error) {
      console.error("Error updating payout frequency:", error);
    }
  }

  const goToTransactions = () => {
    navigate(`/admin/users/${selectedUser.id}/fleet-owner/transactions`)
  }

  const goToPayoutRequests = () => {
    navigate(`/admin/users/${selectedUser.id}/fleet-owner/payouts`)
  }

  const copyToClipboard = (text: string | undefined) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    message.success("Copied to clipboard");
  }

  return (
    <div className="border rounded-lg overflow-hidden bg-white">
      <div className="bg-blue-200 p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-blue-800 flex items-center justify-center">
          <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M15 9C15 7.34315 13.6569 6 12 6C10.3431 6 9 7.34315 9 9C9 10.6569 10.3431 12 12 12C13.6569 12 15 13.3431 15 15C15 16.6569 13.6569 18 12 18C10.3431 18 9 16.6569 9 15"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <h2 className="text-xl font-medium text-blue-800 capitalize">{selectedUser.companyName}</h2>
      </div>

      <div className="p-5 space-y-5">
        <div>
          <div className="text-gray-700 font-medium mb-1">Status</div>
          <div className="flex items-center gap-2">
            <Switch
              switchValue={switchValue}
              setSwitchValue={(value: any) => {
                setSwitchValue(value);
                void toggleSwitch()
              }}
            />
            {
              switchValue
                ? <span className="text-blue-600 font-medium capitalize">{selectedUser.approved}</span>
                : <span className=" text-gray-400 font-medium capitalize">{selectedUser.approved}</span>
            }
            {updatingStatus && <LoadingSpinner color="grey" />}
          </div>
        </div>

        <div>
          <div className="text-gray-500">{selectedUser?.email}</div>
        </div>

        <div className="flex items-center gap-1">
          <span className="text-blue-600 font-bold">{selectedUser?.statistics.totalCompletedTrips} / {selectedUser?.statistics.totalAssignedTrips}</span>
          <span className="text-gray-500 text-sm">Trips Completed</span>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col justify-center items-center">
            <div className="text-4xl font-bold text-gray-800 uppercase ">{formatLargeNumber(selectedUser?.statistics.totalEarned)}</div>
            <div className="text-gray-500 text-sm">Amount Earned</div>
          </div>
          <div className="flex flex-col justify-center items-center">
            <div className="text-lg font-bold text-gray-800 uppercase">{selectedUser?.statistics.averageResponseTime} Mins</div>
            <div className="text-gray-500 text-sm">Average RT</div>
          </div>
        </div>

        <div>
          <div className="text-blue-600">
            {selectedUser.address}
          </div>
        </div>
        <div>
          <h3 className="text-base font-semibold mb-2">
            Payout Frequency
          </h3>
          <Radio.Group
            value={payoutFrequency}
            onChange={(e) => {
              setPayoutFrequency(e.target.value);
              void updateFrequencyValue(e.target.value);
            }}
          >
            <Radio.Button value="weekly">
              Weekly
              {updatingFrequency && payoutFrequency === 'weekly' && <LoadingSpinner color="grey" />}
            </Radio.Button>
            <Radio.Button value="bi-weekly">
              Bi-Weekly
              {updatingFrequency && payoutFrequency === 'bi-weekly' && <LoadingSpinner color="grey" />}
            </Radio.Button>
            <Radio.Button value="monthly">
              Monthly
              {updatingFrequency && payoutFrequency === 'monthly' && <LoadingSpinner color="grey" />}
            </Radio.Button>
          </Radio.Group>
        </div>

        <div>
          <h3 className="text-base font-semibold mb-2">
            Next Payout 
          </h3>
          <p className="text-primary font-semibold">
            {dayjs(selectedUser.nextPayoutDate).isValid()
            ? dayjs(selectedUser.nextPayoutDate).format('MMMM D, YYYY')
            : 'N/A'}
          </p>
        </div>

        <div>
          <h3 className="text-base font-semibold mb-2">
            Total Payable 
          </h3>
          <p className="text-primary font-semibold">
            {formatLargeNumber(selectedUser.totalAmountPayable)}
          </p>
        </div>

        <div>
          <h3 className="text-base font-semibold mb-2">
            Settlement Account 
          </h3>
          <Popover
            placement="bottomLeft"
            title="Settlement Account Details"
            content={<>
              <div className="flex flex-col items-start gap-1 p-4">
                  <span>
                      Bank: {" "}
                      <strong className="font-medium">
                        {selectedUser?.activeSettlementAccount?.bankName}
                      </strong>
                  </span>
                  <span>
                      Account Name: {" "}
                      <strong className="font-medium">
                        {selectedUser?.activeSettlementAccount?.accountName}
                      </strong>
                  </span>                            
                  <span className="flex items-center gap-2">
                      Account Number: {" "}
                      <strong className="font-medium">{selectedUser?.activeSettlementAccount?.accountNumber}</strong>
                      <Copy size={14} className="text-primary cursor-pointer" onClick={() => copyToClipboard(selectedUser?.activeSettlementAccount?.accountNumber)} />
                  </span>
              </div>          
            </>}
          >
            <Button className="text-white">
              <span>View</span>
              <Eye />
            </Button>
          </Popover>
        </div>

        {/* <div>
          <h3 className="text-primary mb-2">
            Discount
          </h3>
        <div className="flex items-center gap-2">
            <Switch switchValue={addDiscount} setSwitchValue={setAddDiscount} />
            {
              addDiscount ? <span className="text-blue-600 font-medium">Enabled</span> :
                <span className=" text-gray-400 font-medium">Disabled</span>
            }
          </div>
        </div> */}

        {/* <div className="flex items-center justify-between bg-gray-50 p-2 rounded">
          <div className="text-amber-500 font-mono text-sm truncate max-w-[200px]">{apiKey}</div>
          <Button className="bg-transparent" variant="ghost" size="icon" onClick={copyApiKey}>
            <Copy className="h-4 w-4" />
            <span className="sr-only">Copy API key</span>
          </Button>
        </div>

        <div>
          <Button
            variant="ghost"
            className="bg-transparent flex items-center gap-1 p-0 hover:bg-transparent hover:text-blue-600"
            onClick={regenerateKey}
          >
            <RefreshCw className="h-4 w-4" />
            <span>Regenerate key</span>
          </Button>
        </div> */}

        <div className="space-y-2">
          <h3>Links</h3>

          <div className="flex flex-col items-start gap-1">
            <Button variant="link" className="bg-transparent text-amber-500 p-0 underline" onClick={() => goToTransactions()}>
              View Transactions
            </Button>

            <Button variant="link" className="bg-transparent text-amber-500 p-0 underline" onClick={() => goToPayoutRequests()}>
              View Payouts
            </Button>
          </div>
        </div>

        {/* <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">Save</Button> */}
      </div>
    </div>
  )
}


function ManageFleetOwners({ selectedUser, handleClosePanel }: { selectedUser: FleetOwner, handleClosePanel: () => void }) {
  return (
    <div key={selectedUser?.id} className="bg-white rounded-lg shadow p-6 transition-all duration-300">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-blue-600">Manage FleetOwner</h2>
        <Button variant="ghost" size="icon" onClick={handleClosePanel} className="hover:bg-gray-100">
          <X className="h-5 w-5 text-red-500" />
        </Button>
      </div>


      <OwnerDetailsCard selectedUser={selectedUser} />
    </div>
  );
}

export default ManageFleetOwners;